import {Component, EventEmitter, Input, Output} from '@angular/core';
import {MatDialog} from '@angular/material/dialog';
import {COMMON_SCOPES, isReservedDocumentKey, RateConfig, RateConfigDocument} from "../../../../model/rate/RateConfig";
import {ConfirmDialogComponent} from "../../confirm-dialog/confirm-dialog.component";
import {PromptDialogComponent} from "../../prompt-dialog/prompt-dialog.component";
import {LanguagePipe} from "../../../pipe/language.pipe";

/**
 * Manages the set of scope tabs (top-level keys of a {@link RateConfigDocument}
 * other than the reserved base-level keys — see {@link isReservedDocumentKey}:
 * `_previewPageCounts`, `_previewBookQuantities`, `facilities`).
 *
 * Emits `scopesMutated` whenever a scope is added/renamed/deleted along with
 * the new active scope; the parent reapplies it to the canonical document.
 */
@Component({
  selector: 'app-rate-scope-tabs',
  templateUrl: './scope-tabs.component.html',
  styleUrls: ['./scope-tabs.component.css']
})
export class ScopeTabsComponent {

  @Input() doc: RateConfigDocument = {};
  @Input() activeScope: string = '';
  @Input() readOnly: boolean = false;

  @Output() activeScopeChange = new EventEmitter<string>();
  @Output() docChange = new EventEmitter<RateConfigDocument>();

  constructor(private dialog: MatDialog) {}

  /** All scope keys in document insertion order (reserved base-level keys excluded). */
  get scopeNames(): string[] {
    return Object.keys(this.doc || {}).filter(k => !isReservedDocumentKey(k));
  }

  /** Common scopes not yet present, used to seed the "add scope" suggestions. */
  get suggestionScopes(): string[] {
    const existing = new Set(this.scopeNames);
    return COMMON_SCOPES.filter(s => !existing.has(s));
  }

  setActive(scope: string): void {
    this.activeScopeChange.emit(scope);
  }

  /** Add a brand-new scope with a minimal empty {@link RateConfig}. */
  addScope(name: string): void {
    if (this.readOnly) { return; }
    const trimmed = (name || '').trim().toUpperCase().replace(/\s+/g, '_');
    if (!trimmed || this.scopeNames.indexOf(trimmed) >= 0 || isReservedDocumentKey(trimmed)) { return; }
    const seed: RateConfig = {version: 3, currency: 'USD', factors: []};
    const next: RateConfigDocument = {...this.doc, [trimmed]: seed};
    this.docChange.emit(next);
    this.activeScopeChange.emit(trimmed);
  }

  /**
   * Rename a scope. Preserves the value verbatim and updates the active
   * pointer if the renamed scope was the active one.
   */
  renameScope(oldName: string, newName: string): void {
    if (this.readOnly) { return; }
    const trimmed = (newName || '').trim().toUpperCase().replace(/\s+/g, '_');
    if (!trimmed || trimmed === oldName) { return; }
    if (this.scopeNames.indexOf(trimmed) >= 0 || isReservedDocumentKey(trimmed)) { return; }
    const next: RateConfigDocument = {};
    // Preserve insertion order while renaming the matching key.
    for (const k of Object.keys(this.doc)) {
      if (k === oldName) {
        next[trimmed] = this.doc[k] as any;
      } else {
        next[k] = this.doc[k] as any;
      }
    }
    this.docChange.emit(next);
    if (this.activeScope === oldName) { this.activeScopeChange.emit(trimmed); }
  }

  /** Delete a scope and pick a sensible new active scope. */
  deleteScope(name: string): void {
    if (this.readOnly) { return; }
    const next: RateConfigDocument = {};
    for (const k of Object.keys(this.doc)) {
      if (k !== name) { next[k] = this.doc[k] as any; }
    }
    this.docChange.emit(next);
    if (this.activeScope === name) {
      const remaining = Object.keys(next).filter(k => !isReservedDocumentKey(k));
      this.activeScopeChange.emit(remaining[0] || '');
    }
  }

  /** Prompt-driven add for the toolbar Add button. */
  promptAdd(): void {
    if (this.readOnly) { return; }
    // Only prepopulate when a common scope is still available; if the user has
    // already added every common scope, leave the field blank so they must
    // type a fully-custom name (no risk of suggesting a duplicate).
    const suggestion = this.suggestionScopes[0] || '';
    const ref = this.dialog.open(PromptDialogComponent, {
      width: '400px',
      data: {
        title: LanguagePipe.get('Add scope', 'compressSpaces'),
        body: '',
        label: LanguagePipe.get('Scope name', 'compressSpaces'),
        initialValue: suggestion,
        okButton: LanguagePipe.get('Add', 'compressSpaces'),
        cancelButton: LanguagePipe.get('Cancel', 'compressSpaces')
      }
    });
    ref.afterClosed().subscribe(name => {
      if (name) { this.addScope(name); }
    });
  }

  /** Prompt-driven rename for the per-tab pencil button. */
  promptRename(name: string, evt: Event): void {
    evt.stopPropagation();
    if (this.readOnly) { return; }
    const ref = this.dialog.open(PromptDialogComponent, {
      width: '400px',
      data: {
        title: LanguagePipe.get('Rename scope', 'compressSpaces'),
        body: '',
        label: LanguagePipe.get('Scope name', 'compressSpaces'),
        initialValue: name,
        okButton: LanguagePipe.get('Rename', 'compressSpaces'),
        cancelButton: LanguagePipe.get('Cancel', 'compressSpaces')
      }
    });
    ref.afterClosed().subscribe(next => {
      if (next) { this.renameScope(name, next); }
    });
  }

  /** Confirm-driven delete for the per-tab trash button. */
  confirmDelete(name: string, evt: Event): void {
    evt.stopPropagation();
    if (this.readOnly) { return; }
    const ref = this.dialog.open(ConfirmDialogComponent, {
      width: '400px',
      data: {
        title: LanguagePipe.get('Delete scope', 'compressSpaces') + ' "' + name + '"' + LanguagePipe.get('?', 'compressSpaces'),
        body: LanguagePipe.get('This cannot be undone.', 'compressSpaces'),
        noButton: LanguagePipe.get('Cancel', 'compressSpaces'),
        yesButton: LanguagePipe.get('Delete', 'compressSpaces'),
        returnValue: 'ok'
      }
    });
    ref.afterClosed().subscribe(result => {
      if (result === 'ok') { this.deleteScope(name); }
    });
  }
}

